import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import AdminPanel from './AdminPanel';
import { CREATE_WORD, UPDATE_WORD, DELETE_WORD } from '../graphql/mutations';

/**
 * Toast — Inkwell editorial inline feedback.
 */
function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 18px',
        background: type === 'error' ? 'var(--vermillion)' : 'var(--ink)',
        color: '#fff',
        borderRadius: 'var(--r2)',
        fontSize: 14,
        fontWeight: 500,
        zIndex: 10000,
        boxShadow: 'var(--sh3)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {message}
    </div>
  );
}

/**
 * WordCard — Inkwell editorial word tile.
 */
function WordCard({ word, onEdit, onDelete, confirmDeleteId }) {
  const isConfirming = confirmDeleteId === word.id;
  return (
    <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--font-han)',
              fontSize: 56,
              lineHeight: 1,
              letterSpacing: '0.04em',
              marginBottom: 4,
            }}
          >
            {word.hanzi}
          </div>
          <div className="pinyin" style={{ fontSize: 16 }}>{word.pinyin}</div>
        </div>
        {word.audio && (
          <span
            className="badge"
            title="Has audio"
            style={{ alignSelf: 'flex-start' }}
          >
            🔊
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {word.hskLevel && <span className="badge badge--mono">HSK {word.hskLevel}</span>}
        {word.difficulty && <span className="badge">{word.difficulty}</span>}
      </div>

      {word.meanings && word.meanings.length > 0 && (
        <div
          style={{
            fontSize: 14,
            color: 'var(--ink-2)',
            marginBottom: 8,
            paddingBottom: 12,
            borderBottom: '1px solid var(--rule-2)',
            minHeight: 40,
          }}
        >
          <span
            className="t-mono"
            style={{ fontSize: 11, color: 'var(--ink-3)', marginRight: 6 }}
          >
            {word.meanings[0].partOfSpeech}.
          </span>
          {word.meanings[0].definition}
        </div>
      )}

      {word.meanings && word.meanings.some((m) => m.examples?.length > 0) && (
        <div className="t-small" style={{ marginBottom: 12 }}>
          {word.meanings.reduce((acc, m) => acc + (m.examples?.length || 0), 0)} example(s)
          {word.topics && word.topics.length > 0 && ` · topics: ${word.topics.join(', ')}`}
        </div>
      )}

      <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
        <button
          className="btn btn--ghost btn--sm"
          style={{ flex: 1 }}
          onClick={() => onEdit(word)}
        >
          Edit
        </button>
        {isConfirming ? (
          <button
            className="btn btn--danger btn--sm"
            style={{ flex: 1 }}
            onClick={() => onDelete(word.id)}
          >
            Click again to confirm
          </button>
        ) : (
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => onDelete(word.id)}
            style={{ color: 'var(--vermillion)' }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function AdminPage({ allWords, onRefresh, onBack }) {
  // Auth is handled at the App level — anyone reaching this page is already
  // authenticated as a teacher, and ownership is enforced server-side per row.
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [toast, setToast] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const confirmTimerRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);
  const dismissToast = useCallback(() => setToast(null), []);

  const [createWord, { loading: creating }] = useMutation(CREATE_WORD);
  const [updateWord, { loading: updating }] = useMutation(UPDATE_WORD);
  const [deleteWord, { loading: deleting }] = useMutation(DELETE_WORD);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredWords = useMemo(() => {
    let filtered = allWords;
    if (filterDifficulty !== 'all') {
      filtered = filtered.filter((w) => w.difficulty === filterDifficulty);
    }
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.hanzi?.toLowerCase().includes(query) ||
          w.pinyin?.toLowerCase().includes(query) ||
          w.meanings?.some((m) => m.definition?.toLowerCase().includes(query))
      );
    }
    return filtered;
  }, [allWords, debouncedSearch, filterDifficulty]);

  const handleCreateWord = useCallback(
    async (wordData) => {
      try {
        await createWord({ variables: { input: wordData } });
        setShowAddPanel(false);
        onRefresh();
        showToast('Word created');
      } catch (error) {
        showToast(`Failed to create: ${error.message}`, 'error');
      }
    },
    [createWord, onRefresh, showToast]
  );

  const handleEdit = useCallback((word) => setEditingWord(word), []);

  const handleSaveEdit = useCallback(
    async (updatedWordData) => {
      try {
        // The form's whitelist already strips non-schema fields, so the
        // payload IS the input. The id has to come from `editingWord` since
        // the form no longer carries it.
        const id = editingWord?.id;
        if (!id) throw new Error('Missing word id');
        await updateWord({ variables: { id, input: updatedWordData } });
        setEditingWord(null);
        onRefresh();
        showToast('Word updated');
      } catch (error) {
        showToast(`Failed to update: ${error.message}`, 'error');
      }
    },
    [editingWord, updateWord, onRefresh, showToast]
  );

  const handleDelete = useCallback(
    async (wordId) => {
      if (confirmDeleteId === wordId) {
        clearTimeout(confirmTimerRef.current);
        setConfirmDeleteId(null);
        try {
          await deleteWord({ variables: { id: wordId } });
          onRefresh();
          showToast('Word deleted');
        } catch (error) {
          showToast(`Failed to delete: ${error.message}`, 'error');
        }
      } else {
        setConfirmDeleteId(wordId);
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = setTimeout(() => setConfirmDeleteId(null), 3000);
      }
    },
    [confirmDeleteId, deleteWord, onRefresh, showToast]
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}

      <header className="ink-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>← Flashcards</button>
          <span style={{ fontWeight: 500 }}>Word library</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--accent" onClick={() => setShowAddPanel(true)}>
            + Add new word
          </button>
        </div>
      </header>

      <div style={{ padding: '32px 40px' }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 32, marginBottom: 24, alignItems: 'baseline' }}>
          <div>
            <div className="t-eyebrow">Total words</div>
            <div style={{ fontSize: 32, fontWeight: 500, marginTop: 4 }}>{allWords.length}</div>
          </div>
          <div style={{ width: 1, height: 32, background: 'var(--rule)' }} />
          <div>
            <div className="t-eyebrow">Showing</div>
            <div style={{ fontSize: 32, fontWeight: 500, marginTop: 4 }}>{filteredWords.length}</div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <input
              className="input"
              placeholder="Search by character, pinyin, or meaning…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="select"
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            style={{ width: 200 }}
          >
            <option value="all">All levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* Grid */}
        {filteredWords.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <p className="t-body">No words found matching your criteria.</p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            {filteredWords.map((word) => (
              <WordCard
                key={word.id}
                word={word}
                onEdit={handleEdit}
                onDelete={handleDelete}
                confirmDeleteId={confirmDeleteId}
              />
            ))}
          </div>
        )}
      </div>

      {(showAddPanel || editingWord) && (
        <AdminPanel
          onClose={() => {
            setShowAddPanel(false);
            setEditingWord(null);
          }}
          onSave={editingWord ? handleSaveEdit : handleCreateWord}
          initialData={editingWord}
          isLoading={creating || updating}
        />
      )}

      {(creating || updating || deleting) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(20,17,13,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            color: '#fff',
            fontFamily: 'var(--font-sans)',
            fontSize: 18,
            fontWeight: 500,
          }}
        >
          {creating && 'Creating word…'}
          {updating && 'Updating word…'}
          {deleting && 'Deleting word…'}
        </div>
      )}
    </div>
  );
}

export default AdminPage;
